/**
 * 主应用组件 — 五功能模块路由架构
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ToastProvider } from '@/components/Toast';
import { ConfirmProvider } from '@/components/ConfirmModal';
import FunctionLayout from '@/layouts/FunctionLayout';
import MainLayout from '@/layouts/MainLayout';
import { BookOpen, GraduationCap, Share2, ClipboardList, FileText, Users, Shield, Activity, MessageSquare, Boxes, Layers } from 'lucide-react';
import FunctionSelector from '@/pages/FunctionSelector';
import LoginPage from '@/pages/Login';
import KnowledgePage from '@/pages/Knowledge';
import UsersPage from '@/pages/Users';
import RolesPage from '@/pages/Roles';
import KnowledgeChatPage from '@/pages/KnowledgeChat';
import MyTasksPage from '@/pages/MyTasks';
import TaskManagementPage from '@/pages/TaskManagement';
import ForceChangePasswordPage from '@/pages/ForceChangePassword';
import DepartmentsPage from '@/pages/Departments';
import OperationLogsPage from '@/pages/OperationLogs';
import SystemConfigPage from '@/pages/SystemConfig';
import WorkRecordPage from '@/pages/WorkRecord';
import DrawingMaterialsPage from '@/pages/DrawingMaterials';
import DrawingLibraryPage from '@/pages/DrawingLibrary';
import PromotionManagePage from '@/pages/PromotionManage';
import PromotionBrowsePage from '@/pages/PromotionBrowse';
import CustomerManagement from '@/pages/CustomerManagement';
import SupplierManagement from '@/pages/SupplierManagement';
import ApprovalDashboard from '@/pages/ApprovalDashboard';
import CommentReview from '@/pages/CommentReview';
import PhoneWhitelist from '@/pages/PhoneWhitelist';
import PromotionFeed from '@/pages/PromotionFeed';
import MessageManagement from '@/pages/MessageManagement';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}><div className="loading-spinner"/></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PermissionRoute({ children, permission }: { children: React.ReactNode; permission: string }) {
  const { hasPermission } = useAuth();
  if (!hasPermission(permission)) return <Navigate to="/select-function" replace />;
  return <>{children}</>;
}

const isMobile = window.innerWidth <= 768;
const customerPath = isMobile ? '/share/feed' : '/share/browse';

function ShareIndexRoute() {
  const role = localStorage.getItem('currentFunctionRole') || '';
  if (role === '销售') return <Navigate to="/share/comment-review" replace />;
  if (role === '研发') return <Navigate to="/share/approvals" replace />;
  if (role === '采购') return <Navigate to="/share/approvals" replace />;
  if (role === '供应商') return <Navigate to="/share/manage" replace />;
  if (role === '客户') return <Navigate to={customerPath} replace />;
  return <Navigate to="/share/manage" replace />;
}

function DefaultRoute() {
  const { hasPermission, user, currentFunction } = useAuth();
  const fn = currentFunction || localStorage.getItem('currentFunction') || '';

  // 每个功能按侧边栏顺序定义路由，取第一个有权限的
  const funcRoutes: Record<string, Array<{ path: string; perm: string }>> = {
    admin: [
      { path: '/admin/system', perm: 'admin:用户管理' },
      { path: '/admin/roles', perm: 'admin:角色管理' },
      { path: '/admin/departments', perm: 'admin:部门管理' },
      { path: '/admin/users', perm: 'admin:用户管理' },
      { path: '/admin/logs', perm: 'admin:操作日志' },
    ],
    exam: [
      { path: '/exam/knowledge', perm: 'exam:知识管理' },
      { path: '/exam/task-management', perm: 'exam:任务管理' },
      { path: '/exam/my-tasks', perm: 'exam:我的任务' },
    ],
    knowledge: [
      { path: '/knowledge/archives', perm: 'knowledge:知识管理' },
      { path: '/knowledge/chat', perm: 'knowledge:知识问答对话' },
    ],
    share: (() => {
      const role = localStorage.getItem('currentFunctionRole') || '';
      if (role === '销售') return [{ path: '/share/comment-review', perm: 'share:客户管理' }, { path: '/share/messages', perm: 'share:客户管理' }, { path: '/share/whitelist', perm: 'share:客户管理' }, { path: '/share/customers', perm: 'share:客户管理' }];
      if (role === '研发') return [{ path: '/share/approvals', perm: 'share:档案管理' }, { path: '/share/manage', perm: 'share:档案管理' }];
      if (role === '采购') return [{ path: '/share/approvals', perm: 'share:档案管理' }, { path: '/share/suppliers', perm: 'share:供应商管理' }];
      if (role === '供应商') return [{ path: '/share/manage', perm: 'share:档案管理' }];
      return [{ path: '/share/feed', perm: 'share:预览浏览' }].map(p => ({...p, path: p.path === '/share/feed' ? customerPath : p.path}));
    })(),
    work: [
      { path: '/work/records', perm: 'work:日常工作' },
    ],
    drawing: [
      { path: '/drawing/materials', perm: 'drawing:素材管理' },
      { path: '/drawing/library', perm: 'drawing:产品管理' },
    ],
  };

  const routes = funcRoutes[fn];
  if (routes) {
    const first = routes.find(r => hasPermission(r.perm));
    if (first) return <Navigate to={first.path} replace />;
    // 权限未加载时，根据角色兜底
    const role = localStorage.getItem('currentFunctionRole') || '';
    if (role === '研发') return <Navigate to="/share/approvals" replace />;
    if (role === '供应商') return <Navigate to="/share/manage" replace />;
    if (role === '销售') return <Navigate to="/share/customers" replace />;
    if (role === '采购') return <Navigate to="/share/approvals" replace />;
    if (role === '客户') return <Navigate to={customerPath} replace />;
    // 如果路由表有值但权限都不匹配，用第一条兜底
    if (routes.length > 0) return <Navigate to={routes[0].path} replace />;
  }
  if (fn === 'share') return <Navigate to="/share/browse" replace />;
  if (fn === 'exam') return <Navigate to="/exam/my-tasks" replace />;
  if (fn === 'work') return <Navigate to="/work/records" replace />;
  if (fn === 'knowledge') return <Navigate to="/knowledge/chat" replace />;
  if (fn === 'admin') return <Navigate to="/admin/system" replace />;
  if (fn === 'drawing') return <Navigate to="/drawing/materials" replace />;
  return <Navigate to="/select-function" replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/force-change-password" element={<ProtectedRoute><ForceChangePasswordPage /></ProtectedRoute>} />

      {/* 功能选择页 */}
      <Route path="/select-function" element={<ProtectedRoute><FunctionSelector /></ProtectedRoute>} />

      {/* ===== 学习考试 ===== */}
      <Route path="/exam" element={<ProtectedRoute><FunctionLayout title="学习考试" functionKey="exam" navItems={[
        { path: '/exam/knowledge', label: '学习档案', icon: <BookOpen size={20} strokeWidth={1.5} />, permission: 'exam:知识管理' },
        { path: '/exam/task-management', label: '任务管理', icon: <ClipboardList size={20} strokeWidth={1.5} />, permission: 'exam:任务管理' },
        { path: '/exam/my-tasks', label: '我的任务', icon: <ClipboardList size={20} strokeWidth={1.5} />, permission: 'exam:我的任务' },
      ]} /></ProtectedRoute>}>
        <Route index element={<Navigate to="/exam/my-tasks" replace />} />
        <Route path="knowledge" element={<KnowledgePage />} />
        <Route path="task-management" element={<TaskManagementPage />} />
        <Route path="my-tasks" element={<MyTasksPage />} />
      </Route>

      {/* ===== 知识传承 ===== */}
      <Route path="/knowledge" element={<ProtectedRoute><FunctionLayout title="知识传承" functionKey="knowledge" navItems={[
        { path: '/knowledge/archives', label: '知识管理', icon: <BookOpen size={20} strokeWidth={1.5} />, permission: 'knowledge:知识管理' },
        { path: '/knowledge/chat', label: '知识问答', icon: <MessageSquare size={20} strokeWidth={1.5} />, permission: 'knowledge:知识问答对话' },
      ]} /></ProtectedRoute>}>
        <Route index element={<Navigate to="/knowledge/archives" replace />} />
        <Route path="archives" element={<KnowledgePage />} />
        <Route path="chat" element={<KnowledgeChatPage />} />
      </Route>

      {/* ===== 分享平台 ===== */}
      <Route path="/share" element={<ProtectedRoute><FunctionLayout title="分享平台" functionKey="share" navItems={[
        { path: '/share/customers', label: '客户管理', icon: <Users size={20} strokeWidth={1.5} />, permission: 'share:客户管理' },
        { path: '/share/comment-review', label: '评论审核', icon: <MessageSquare size={20} strokeWidth={1.5} />, permission: 'share:客户管理', roles: ['销售'] },
        { path: '/share/messages', label: '留言管理', icon: <MessageSquare size={20} strokeWidth={1.5} />, permission: 'share:客户管理', roles: ['销售'] },
        { path: '/share/whitelist', label: '白名单管理', icon: <MessageSquare size={20} strokeWidth={1.5} />, permission: 'share:客户管理', roles: ['销售'] },
        { path: '/share/approvals', label: '视频审核', icon: <Shield size={20} strokeWidth={1.5} />, permission: 'share:档案管理', roles: ['研发', '采购'] },
        { path: '/share/suppliers', label: '供应商管理', icon: <Users size={20} strokeWidth={1.5} />, permission: 'share:供应商管理' },
        { path: '/share/manage', label: '视频上传', icon: <Share2 size={20} strokeWidth={1.5} />, permission: 'share:档案管理', roles: ['供应商'] },
      ]} /></ProtectedRoute>}>
        <Route index element={<ShareIndexRoute />} />
        <Route path="manage" element={<PromotionManagePage />} />
        <Route path="customers" element={<CustomerManagement />} />
        <Route path="suppliers" element={<SupplierManagement />} />
        <Route path="approvals" element={<ApprovalDashboard />} />
        <Route path="comment-review" element={<CommentReview />} />
        <Route path="messages" element={<MessageManagement />} />
        <Route path="whitelist" element={<PhoneWhitelist />} />
      </Route>
      {/* 分享平台-浏览（客户，无侧边栏） */}
      <Route path="/share/browse" element={<ProtectedRoute><PromotionBrowsePage /></ProtectedRoute>} />
      <Route path="/share/feed" element={<ProtectedRoute><PromotionFeed /></ProtectedRoute>} />

      {/* ===== 日常工作 ===== */}
      <Route path="/work" element={<ProtectedRoute><FunctionLayout title="日常工作" functionKey="work" navItems={[
        { path: '/work/records', label: '工作记录', icon: <ClipboardList size={20} strokeWidth={1.5} />, permission: 'work:日常工作' },
      ]} /></ProtectedRoute>}>
        <Route index element={<Navigate to="/work/records" replace />} />
        <Route path="records" element={<WorkRecordPage />} />
      </Route>

      {/* ===== 图纸管理 ===== */}
      <Route path="/drawing" element={<ProtectedRoute><FunctionLayout title="图纸管理" functionKey="drawing" navItems={[
        { path: '/drawing/materials', label: '素材库', icon: <Boxes size={20} strokeWidth={1.5} />, permission: 'drawing:素材管理' },
        { path: '/drawing/library', label: '图纸库', icon: <Layers size={20} strokeWidth={1.5} />, permission: 'drawing:产品管理' },
      ]} /></ProtectedRoute>}>
        <Route index element={<Navigate to="/drawing/materials" replace />} />
        <Route path="materials" element={<DrawingMaterialsPage />} />
        <Route path="library" element={<DrawingLibraryPage />} />
      </Route>

      {/* ===== 管理员 ===== */}
      <Route path="/admin" element={<ProtectedRoute><FunctionLayout title="管理员" functionKey="admin" navItems={[
        { path: '/admin/system', label: '系统管理', icon: <Shield size={20} strokeWidth={1.5} />, permission: 'admin:用户管理' },
        { path: '/admin/roles', label: '角色管理', icon: <Shield size={20} strokeWidth={1.5} />, permission: 'admin:角色管理' },
        { path: '/admin/departments', label: '部门管理', icon: <FileText size={20} strokeWidth={1.5} />, permission: 'admin:部门管理' },
        { path: '/admin/users', label: '用户管理', icon: <Users size={20} strokeWidth={1.5} />, permission: 'admin:用户管理' },
        { path: '/admin/logs', label: '操作日志', icon: <Activity size={20} strokeWidth={1.5} />, permission: 'admin:操作日志' },
      ]} /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/system" replace />} />
        <Route path="system" element={<SystemConfigPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="departments" element={<DepartmentsPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="logs" element={<OperationLogsPage />} />
      </Route>

      {/* ===== 旧路由兼容 ===== */}
      <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route index element={<DefaultRoute />} />
        <Route path="knowledge" element={<KnowledgePage />} />
        <Route path="knowledge-chat" element={<KnowledgeChatPage />} />
        <Route path="task-management" element={<TaskManagementPage />} />
        <Route path="my-tasks" element={<MyTasksPage />} />
        <Route path="work-record" element={<WorkRecordPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="departments" element={<DepartmentsPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="operation-logs" element={<OperationLogsPage />} />
      </Route>

      {/* standalone promotion pages (backward compat) */}
      <Route path="/promotion/manage" element={<ProtectedRoute><PromotionManagePage /></ProtectedRoute>} />
      <Route path="/promotion/browse" element={<ProtectedRoute><PromotionBrowsePage /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <ConfirmProvider>
          <AppRoutes />
        </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
